import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { List, X, Globe, CaretDown, SignOut, UserCircle, ShieldCheck } from "@phosphor-icons/react";
import { useT, LANGS } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "./Reveal";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Navbar = () => {
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/biens", label: t("nav.catalog") },
    { to: "/#simulator", label: t("nav.simulator") },
    { to: "/cout-de-la-vie", label: "Coût de la vie" },
    { to: "/blog", label: "Blog" },
    { to: "/#contact", label: t("nav.contact") },
  ];

  const goLink = (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  const FLAGS = { fr: "https://flagcdn.com/24x18/fr.png", en: "https://flagcdn.com/24x18/gb.png", ar: "https://flagcdn.com/24x18/ma.png" };
  const langNames = { fr: "FR", en: "EN", ar: "ع" };
  const langFull = { fr: "Français", en: "English", ar: "العربية" };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-navy/10 shadow-sm" : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="flex items-center shrink-0">
          <img src={LOGO_URL} alt="YADIMMO" className="h-8 sm:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-link-${l.to.replace(/[/#]/g, "") || "home"}`}
              onClick={() => goLink(l.to)}
              className="text-sm font-medium text-navy/80 hover:text-gold transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 start-0 w-0 h-px bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-switcher" className="flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold transition-colors px-2 py-1">
                <img src={FLAGS[lang]} alt={lang} className="w-5 h-auto rounded-[2px]" /> {langNames[lang]} <CaretDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              {LANGS.map((lg) => (
                <DropdownMenuItem key={lg} data-testid={`lang-option-${lg}`} onClick={() => setLang(lg)} className="cursor-pointer">
                  <img src={FLAGS[lg]} alt={lg} className="w-5 h-auto rounded-[2px] me-2" />
                  {langFull[lg]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && user.email ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="hidden sm:flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors">
                  <UserCircle size={22} weight="light" />
                  <span className="max-w-[110px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/espace")} className="cursor-pointer">
                  <UserCircle size={16} className="me-2" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer">
                    <ShieldCheck size={16} className="me-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer text-destructive">
                  <SignOut size={16} className="me-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <button data-testid="nav-login" onClick={() => navigate("/connexion")} className="text-sm font-medium text-navy hover:text-gold transition-colors px-3 py-2">
                {t("nav.login")}
              </button>
              <button data-testid="nav-register" onClick={() => navigate("/inscription")} className="text-sm font-semibold bg-navy text-white px-5 py-2.5 rounded-full hover:bg-[#254B75] transition-colors">
                {t("nav.register")}
              </button>
            </div>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden text-navy p-1">
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-navy/10 px-5 py-4 flex flex-col gap-1 shadow-lg">
          {links.map((l) => (
            <button key={l.to} onClick={() => goLink(l.to)} className="text-start py-3 text-navy font-medium border-b border-navy/5">
              {l.label}
            </button>
          ))}
          {user && user.email ? (
            <>
              <button onClick={() => navigate("/espace")} className="text-start py-3 text-navy font-medium">{t("nav.dashboard")}</button>
              {user.role === "admin" && <button onClick={() => navigate("/admin")} className="text-start py-3 text-navy font-medium">{t("nav.admin")}</button>}
              <button onClick={async () => { await logout(); navigate("/"); }} className="text-start py-3 text-destructive font-medium">{t("nav.logout")}</button>
            </>
          ) : (
            <div className="flex gap-3 pt-3">
              <button onClick={() => navigate("/connexion")} className="flex-1 py-3 rounded-full border border-navy text-navy font-semibold">{t("nav.login")}</button>
              <button onClick={() => navigate("/inscription")} className="flex-1 py-3 rounded-full bg-navy text-white font-semibold">{t("nav.register")}</button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
